import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, User, Shield, Briefcase, Headphones, Pencil, Loader2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import EditTeamMemberModal from "../componants/EditTeamMemberModal";
import { motion, AnimatePresence } from "framer-motion";

const TeamNodeItem = ({ node, onEdit }: { node: any, onEdit: (u: any) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  const getIcon = (role: string) => {
    const iconBase = "w-12 h-12 flex items-center justify-center rounded-2xl transition-all ";
    switch(role) {
      case "ADMIN": return <div className={`${iconBase} bg-red-50 text-red-500 shadow-sm border border-red-100`}><Shield size={24}/></div>;
      case "MANAGER": return <div className={`${iconBase} bg-emerald-50 text-[#059669] shadow-sm border border-emerald-100`}><Briefcase size={24}/></div>;
      case "TEAM_LEADER": return <div className={`${iconBase} bg-blue-50 text-blue-500 shadow-sm border border-blue-100`}><User size={24}/></div>;
      case "AGENT": return <div className={`${iconBase} bg-indigo-50 text-indigo-500 shadow-sm border border-indigo-100`}><Headphones size={24}/></div>;
      default: return <div className={`${iconBase} bg-slate-50 text-slate-500 shadow-sm border border-slate-100`}><User size={24}/></div>;
    }
  };

  return (
    <div className="mb-4">
      {/* Node Card - Category Style */}
      <div 
        onClick={() => hasChildren && setIsOpen(!isOpen)}
        className={`flex items-center gap-5 bg-white p-6 rounded-[32px] border-2 transition-all cursor-pointer group 
          ${isOpen ? 'border-[#059669] shadow-lg' : 'border-transparent hover:border-slate-100 hover:shadow-md'}`}
      >
        {/* Profile Icon */}
        {getIcon(node.role)}

        {/* User Details */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-[#0F172A] font-extrabold text-lg tracking-tight uppercase">{node.name}</h4>
            {hasChildren && (
               <span className="text-[10px] font-bold bg-emerald-50 text-[#059669] px-2 py-0.5 rounded-full">
                 {node.children.length} SUBORDINATES
               </span>
            )}
          </div>
          <p className="text-slate-400 text-sm font-medium lowercase tracking-wide">{node.email}</p>
        </div>

        {/* Role & Actions */}
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.15em] bg-slate-50 text-slate-500 px-4 py-2 rounded-xl border border-slate-100">
            {node.role?.replace("_", " ")}
          </span>

          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(node); }}
            className="p-3 bg-slate-50 text-slate-400 hover:bg-[#059669] hover:text-white rounded-2xl transition-all active:scale-90"
          >
            <Pencil size={18} />
          </button>

          {hasChildren && (
            <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#059669]' : 'text-slate-300'}`}>
              <ChevronDown size={22} />
            </div>
          )}
        </div>
      </div>

      {/* Nested Children - Slide Down Animation */}
      <AnimatePresence>
        {hasChildren && isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="overflow-hidden ml-12 pl-8 border-l-2 border-slate-100 relative"
          >
            {/* Design Connectors */}
            <div className="absolute left-0 top-0 w-8 h-8 border-b-2 border-slate-100 rounded-bl-3xl"></div>
            
            {node.children.map((child: any) => (
              <TeamNodeItem key={child.id} node={child} onEdit={onEdit} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Subordinates = () => {
  const { token } = useAdminAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<any>(null);

  const fetchTeam = () => {
    setLoading(true);
    fetch("http://localhost:2027/api/admin/team/my-subordinates", {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => { setTree(data); setLoading(false); })
    .catch(err => { console.error(err); setLoading(false); });
  };

  useEffect(() => { fetchTeam(); }, []);

  return (
    <div className="p-8 bg-[#F8FAFC] min-h-screen font-sans">
      {/* Header Section */}
      <div className="flex justify-between items-end mb-12">
        <div>
          <h2 className="text-4xl font-black text-[#0F172A] tracking-tighter">Hierarchy Explorer</h2>
          <p className="text-slate-500 font-medium mt-2">TRADYFROG MANAGEMENT SUITE</p>
        </div>
        <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Nodes</p>
           <p className="text-2xl font-bold text-[#059669]">{tree.length}</p>
        </div>
      </div>

      {/* Main Tree Container */}
      {loading ? (
        <div className="flex flex-col items-center justify-center p-32 space-y-4">
          <Loader2 className="animate-spin text-[#059669]" size={48} />
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Mapping Structure...</p>
        </div>
      ) : (
        <div className="max-w-5xl mx-auto">
          {tree.length > 0 ? (
            tree.map((node: any) => (
               <TeamNodeItem key={node.id} node={node} onEdit={(u) => setEditingUser(u)} />
            ))
          ) : (
            <div className="bg-white p-20 rounded-[40px] border-2 border-dashed border-slate-200 text-center">
               <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                  <User size={40} />
               </div>
               <p className="text-slate-500 font-bold uppercase tracking-widest">Reporting Structure is Empty</p>
            </div>
          )}
        </div>
      )}

      {/* Modal Integration */}
      {editingUser && (
        <EditTeamMemberModal 
            user={editingUser} 
            onClose={() => setEditingUser(null)} 
            onSuccess={() => {
                setEditingUser(null);
                fetchTeam();
            }}
        />
      )}
    </div>
  );
};

export default Subordinates; 